import { compareVersions } from "compare-versions";
import type { TFunction } from "i18next";
import type { ReleaseNote } from "./releaseNotesData";

const OFFLINE_PREFIX = "Offline ";

export const isOfflineRelease = (release: ReleaseNote) =>
  release.version.startsWith(OFFLINE_PREFIX);

export const stripOffline = (version: string) =>
  version.startsWith(OFFLINE_PREFIX) ? version.slice(OFFLINE_PREFIX.length) : version;

export const displayVersion = (release: ReleaseNote) =>
  isOfflineRelease(release) ? `Oculera ${release.version}` : release.version;

export function latestRelease(releases: ReleaseNote[], offline: boolean) {
  return releases
    .filter((release) => isOfflineRelease(release) === offline)
    .sort((a, b) => compareVersions(stripOffline(b.version), stripOffline(a.version)))[0];
}

export interface DesktopRelease {
  version: string;
  platforms: ("windows" | "macos")[];
}

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function parseDesktopCatalog(value: unknown): DesktopRelease[] {
  if (!record(value) || value.schemaVersion !== 1 || !Array.isArray(value.releases)) {
    throw new Error("Unsupported desktop release catalog");
  }
  const versions = new Set<string>();
  return value.releases.map((entry) => {
    if (
      !record(entry) ||
      typeof entry.version !== "string" ||
      !/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$(?![\s\S])/.test(entry.version) ||
      versions.has(entry.version) ||
      !Array.isArray(entry.platforms) ||
      entry.platforms.length === 0 ||
      !entry.platforms.every((platform) => platform === "windows" || platform === "macos")
    )
      throw new Error("Invalid desktop release");
    versions.add(entry.version);
    return {
      version: entry.version,
      platforms: [...new Set(entry.platforms)] as DesktopRelease["platforms"],
    };
  });
}

export function mergeDesktopReleases(bundled: ReleaseNote[], published: DesktopRelease[]) {
  const merged = new Map(bundled.map((release) => [release.version, release]));
  published.forEach(({ version, platforms }) => {
    const key = `${OFFLINE_PREFIX}${version}`;
    if (!merged.has(key))
      merged.set(key, { version: key, platforms, notesPending: true, summary: "", sections: [] });
  });
  const all = [...merged.values()];
  return [
    ...all
      .filter(isOfflineRelease)
      .sort((a, b) => compareVersions(stripOffline(b.version), stripOffline(a.version))),
    ...all.filter((release) => !isOfflineRelease(release)),
  ];
}

export const releasePath = (release: ReleaseNote) =>
  `/versions/${encodeURIComponent(release.version)}`;

export function latestPublishedDesktop(releases: ReleaseNote[], published: DesktopRelease[]) {
  const publishedVersions = new Set(published.map(({ version }) => `${OFFLINE_PREFIX}${version}`));
  return latestRelease(
    releases.filter((release) => publishedVersions.has(release.version)),
    true,
  );
}

export const DESKTOP_WINDOWS_DOWNLOAD_URL =
  "https://vfa-apk-bucket.s3.eu-central-1.amazonaws.com/OculeraDesktop/oculera-local-windows-latest.zip";

export const offersWindowsDownload = (release: ReleaseNote, current?: ReleaseNote) =>
  release.version === current?.version &&
  (!release.platforms || release.platforms.includes("windows"));

// The tutorial of the newest curated release that is not newer than the published desktop
// version, so users on the published version never get steps for a release they cannot
// install yet. Without the catalog, the newest curated tutorial is used.
export const desktopTutorialRelease = (releases: ReleaseNote[], latestOffline?: ReleaseNote) =>
  latestRelease(
    releases.filter(
      (release) =>
        release.tutorial &&
        (!latestOffline ||
          compareVersions(stripOffline(release.version), stripOffline(latestOffline.version)) <= 0),
    ),
    true,
  );

// Only a version the publication catalog confirms counts as current. While the catalog is
// loading or unreachable there is none, so a bundled note for a release that is not out yet
// never gets the Latest badge or the shared download link, which still serves the old build.
export const currentDesktop = (latestOffline?: ReleaseNote) => latestOffline;

export function releaseCopy(release: ReleaseNote, t: TFunction) {
  if (!release.notesPending) {
    return { summary: release.summary, availability: release.availability };
  }
  const platforms = (release.platforms ?? []).map((platform) =>
    t(`releaseNotes.platform.${platform}`),
  );
  return {
    summary: t("releaseNotes.pendingSummary", { version: stripOffline(release.version) }),
    availability: t("releaseNotes.pendingAvailability", {
      platforms: platforms.join(` ${t("releaseNotes.and")} `),
    }),
  };
}
